import React, { useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useNavigate } from 'react-router-dom';

import '../config/firebase';
import { saveAttendees } from '../database';
import { validateEmail, validateRequired } from '../util/validation';

const attendeeFields = [
    'enrolmentDate',
    'firstName',
    'lastName',
    'email',
    'phone',
    'gender',
    'age',
    'dateOfBirth',
    'baptised',
    'schoolYear',
    'tertiaryStudent',
    'firstAidCert',
    'dietryRequirements',
    'subscribe',
    'accommodation',
    'guardian',
];

// Set up initial form values - required by Formik
const initialValues = {
    mainFields: {
        address1: '',
        address2: '',
        city: '',
        postCode: '',
        country: 'New Zealand',
        northIsland: '',
        ecclesia: '',
        transport: '',
        arrivalDetails: '',
        payment: '',
        capitalFund: 'no',
        tentMattresses: '',
        highChairPortacot: '',
        freeWill: '',
    },
    attendees: [],
};

[...Array(15).keys()].forEach((index) => {
    initialValues.attendees[index] = {};
    attendeeFields.forEach((field) => {
        initialValues.attendees[index][field] = '';
    });
});

function save(values, registrationCode) {
    const registrationDate = Date.now();
    values.attendees = values.attendees.filter(
        (attendee) => attendee.firstName !== ''
    );

    const attendees = values.attendees.map((attendee, index) => {
        let capitalFund;
        let tentMattresses;
        let highChairPortacot;
        let freeWill;
        if (index !== 0) {
            capitalFund = 'No';
            tentMattresses = '';
            highChairPortacot = '';
            freeWill = '';
        } else {
            capitalFund = values.mainFields.capitalFund;
            tentMattresses = values.mainFields.tentMattresses;
            highChairPortacot = values.mainFields.highChairPortacot;
            freeWill = values.mainFields.freeWill;
        }
        attendee.registrationDate = registrationDate;
        attendee.phone =
            attendee.phone !== '' ? attendee.phone : values.attendees[0].phone;
        attendee.email =
            attendee.email !== '' ? attendee.email : values.attendees[0].email;
        attendee.subscribe =
            index === 0 || attendee.email !== values.attendees[0].email
                ? attendee.subscribe
                : 'no';
        return {
            registrationCode,
            ...attendee,
            ...values.mainFields,
            capitalFund,
            tentMattresses,
            highChairPortacot,
            freeWill,
        };
    });
    return saveAttendees(attendees);
}

const EnrolmentForm = () => {
    const navigate = useNavigate();
    const [attendeeCount, setattendeeCount] = useState(0);
    const [agreeToRules, setAgreeToRules] = useState(false);
    const [rulesError, setRulesError] = useState(false);
    const [submissionError, setSubmissionError] = useState(false);
    function updateAttendeeCount(valueAsNumber) {
        setattendeeCount(valueAsNumber);
    }
    return (
        <div className="enrolment-form">
            <h1>Summer School 2026</h1>

            <p className="required-message">
                <strong>N.B. All required fields have a green border.</strong>
            </p>
            <Formik
                initialValues={initialValues}
                onSubmit={(values, { setSubmitting }) => {
                    if (!agreeToRules) {
                        setRulesError(true);
                        setSubmitting(false);
                        return;
                    } else {
                        setRulesError(false);
                        setSubmitting(true);
                    }
                    const postCode = values.mainFields.postCode;
                    const lastName = values.attendees[0].lastName;
                    const registrationCode = `${lastName}${postCode}${
                        Math.floor(Math.random() * (+1000 - +9999)) + +1000
                    }`;
                    save(values, registrationCode)
                        .then(function (test) {
                            setSubmitting(false);
                            navigate(
                                `/success?registrationCode=${registrationCode}`
                            );
                        })
                        .catch(function (error) {
                            setSubmitting(false);
                            setSubmissionError(true);
                        });
                }}
            >
                {({ isSubmitting, errors }) => (
                    <Form>
                        {isSubmitting && (
                            <div className="loading-overlay">
                                <div className="loading-spinner"></div>
                            </div>
                        )}
                        <h2>Main Applicant</h2>
                        <div className="agree-to-rules">
                            <label>
                                I have read and agree to abide by the{' '}
                                <a
                                    rel="noopener noreferrer"
                                    target="_blank"
                                    href="https://cfbs.org.nz/policies/"
                                >
                                    School rules and objectives
                                </a>
                            </label>
                            <input
                                onChange={() => setAgreeToRules(!agreeToRules)}
                                className="required checkbox"
                                type="checkbox"
                                name="attendees[0].agreedToRules"
                                checked={agreeToRules}
                            />

                            {rulesError && (
                                <div className="rules-error">
                                    You must agree before enroling
                                </div>
                            )}
                        </div>
                        <div className="form-field">
                            <label>First name</label>
                            <Field
                                name="attendees[0].firstName"
                                validate={validateRequired}
                                className="required"
                            />
                            <ErrorMessage
                                name="attendees[0].firstName"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>Last name</label>
                            <Field
                                name="attendees[0].lastName"
                                validate={validateRequired}
                                className="required"
                            />
                            <ErrorMessage
                                name="attendees[0].lastName"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>Email</label>
                            <Field
                                name="attendees[0].email"
                                validate={validateEmail}
                                className="required"
                            />
                            <ErrorMessage
                                name="attendees[0].email"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>
                                Subscribe to Bible School Email Updates?
                            </label>
                            <Field
                                component="select"
                                name={`attendees[0].subscribe`}
                            >
                                <option value="">--</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </Field>
                        </div>
                        <div className="form-field">
                            <label>Phone</label>
                            <Field
                                name="attendees[0].phone"
                                validate={validateRequired}
                                className="required"
                            />
                            <ErrorMessage
                                name="attendees[0].phone"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>Sex</label>
                            <Field
                                component="select"
                                name="attendees[0].gender"
                                validate={validateRequired}
                                className="required"
                            >
                                <option value="">--</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                            </Field>
                            <ErrorMessage
                                name="attendees[0].gender"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>Dietary Requirements</label>
                            <Field name={`attendees[0].dietaryRequirements`} />
                        </div>
                        <div className="form-field">
                            <label>Age</label>
                            <Field
                                component="select"
                                name="attendees[0].age"
                                validate={validateRequired}
                                className="required"
                            >
                                <option value="">--</option>
                                <option value="50orOver">50 or over</option>
                                <option value="26-49">26-49</option>
                                <option value="18-25">18-25</option>
                                <option value="under18">Under 18</option>
                            </Field>
                            <ErrorMessage
                                name="attendees[0].age"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>
                                Date of Birth (if 25 or under){' '}
                                <span className="small">dd/mm/yy</span>
                            </label>{' '}
                            <div className="guardian">
                                <Field name="attendees[0].dateOfBirth" />
                                <span className="small">
                                    Used to calculate fees, allocate Junior
                                    School classes and plan young people's
                                    activities.
                                </span>
                            </div>
                        </div>
                        <div className="form-field">
                            <label>Guardian</label>
                            <div className="guardian">
                                <Field name="attendees[0].guardian" />
                                <span className="small">
                                    15-17 year olds attending without their
                                    parents must nominate another attendee as a
                                    guardian. Attendance is subject to approval.
                                </span>
                            </div>
                        </div>
                        <div className="form-field">
                            <label>
                                School Year{' '}
                                <span className="small">(if applicable)</span>
                            </label>
                            <Field name={`attendees[0].schoolYear`} />
                        </div>
                        <div className="form-field">
                            <label>
                                Tertiary student{' '}
                                <span className="small">
                                    (used to calculate fees)
                                </span>
                            </label>
                            <Field
                                component="select"
                                name={`attendees[0].tertiaryStudent`}
                            >
                                <option value="">--</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </Field>
                        </div>
                        <div className="form-field">
                            <label>Do you have a First Aid Certificate?</label>
                            <Field
                                component="select"
                                name={`attendees[0].firstAidCert`}
                            >
                                <option value="">--</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </Field>
                        </div>
                        <div className="form-field">
                            <label>Baptised</label>
                            <Field
                                component="select"
                                name="attendees[0].baptised"
                                validate={validateRequired}
                                className="required"
                            >
                                <option value="">--</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </Field>
                            <ErrorMessage
                                name="attendees[0].baptised"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>Accommodation</label>
                            <Field
                                component="select"
                                name="attendees[0].accommodation"
                                validate={validateRequired}
                                className="required"
                            >
                                <option value="">--</option>
                                <option value="room">Room required</option>
                                <option value="tent-caravan">
                                    Bringing a tent or caravan
                                </option>
                                <option value="off-site">
                                    Staying off site
                                </option>
                            </Field>
                            <ErrorMessage
                                name="attendees[0].accommodation"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>
                                If in a tent do you need mattresses and how
                                many?
                            </label>
                            <Field name="mainFields.tentMattresses" />
                        </div>
                        <div className="form-field">
                            <label>
                                High chair or portacot needed? (specify which)
                            </label>
                            <Field name="mainFields.highChairPortacot" />
                        </div>
                        <div className="form-field">
                            <label>Ecclesia</label>
                            <Field
                                name="mainFields.ecclesia"
                                validate={validateRequired}
                                className="required"
                            />
                            <ErrorMessage
                                name="mainFields.ecclesia"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>Home Address 1</label>
                            <Field
                                name="mainFields.address1"
                                validate={validateRequired}
                                className="required"
                            />
                            <ErrorMessage
                                name="mainFields.address1"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>Home Address 2</label>
                            <Field name="mainFields.address2" />
                        </div>
                        <div className="form-field">
                            <label>City</label>
                            <Field
                                name="mainFields.city"
                                validate={validateRequired}
                                className="required"
                            />
                            <ErrorMessage
                                name="mainFields.city"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>Post code</label>
                            <Field
                                name="mainFields.postCode"
                                validate={validateRequired}
                                className="required"
                            />
                            <ErrorMessage
                                name="mainFields.postCode"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>Country</label>
                            <Field
                                name="mainFields.country"
                                validate={validateRequired}
                                className="required"
                            />
                            <ErrorMessage
                                name="mainFields.country"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>
                                Do you live in South Island of NZ?
                                <br />
                                <span className="small">
                                    Used to calculate fees
                                </span>
                            </label>
                            <Field
                                component="select"
                                name="mainFields.southIsland"
                                validate={validateRequired}
                                className="required"
                            >
                                <option value="">--</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </Field>
                            <ErrorMessage
                                name="mainFields.southIsland"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>How are you getting there?</label>
                            <Field
                                component="select"
                                name="mainFields.transport"
                                validate={validateRequired}
                                className="required"
                            >
                                <option value="">--</option>
                                <option value="needPickUp">
                                    I need picking up
                                </option>
                                <option value="ownTransport">
                                    I have my own transport
                                </option>
                            </Field>
                            <ErrorMessage
                                name="mainFields.transport"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>
                                If you need picking up provide full details,
                                flight numbers, arrival and departure times,
                                etc.
                            </label>
                            <Field
                                component="textarea"
                                name={`mainFields.arrivalDetails`}
                            />
                        </div>
                        {/* <div className="form-field">
                                        <label>If you are asked to be involved with Junior School, do you agree to completing a Police Check and a short training session for child safety purposes? </label>
                                        <div className="guardian">
                                        <Field
                                            component="select"
                                            name={`attendees[0].policeCheck`}
                                            
                                        >
                                            <option value="">--</option>
                                            <option value="yes">Yes</option>
                                            <option value="no">No</option>
                                            <option value="n/a">Not applicable</option>
                                        </Field>
                                        <div className="small">Note, the school will pay for these.</div>
                                        </div>
                                        
                                    </div> */}
                        <div className="form-field">
                            <label>Payment</label>
                            <Field
                                component="select"
                                name="mainFields.payment"
                                validate={validateRequired}
                                className="required"
                            >
                                <option value="">--</option>
                                <option value="directDebit">
                                    I will pay direct debit before the school
                                </option>
                                <option value="payAtSchool">
                                    I will pay at the school because I'm from
                                    overseas
                                </option>
                            </Field>
                            <ErrorMessage
                                name="mainFields.payment"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>
                                Would you like to add a contribution to the
                                Bible School Capital Fund to your fees?
                            </label>
                            <Field
                                component="select"
                                name="mainFields.capitalFund"
                            >
                                <option value="no">No thank you</option>
                                <option value="$10">$10</option>
                                <option value="$20">$20</option>
                                <option value="$50">$50</option>
                            </Field>
                            <ErrorMessage
                                name="mainFields.payment"
                                component="div"
                                className="validation-error"
                            />
                        </div>
                        <div className="form-field">
                            <label>
                                Would you like to include a free will offering
                                for the Memorial meeting in your fees?
                            </label>
                            <Field
                                component="select"
                                name="mainFields.freeWill"
                            >
                                <option value="no">No thank you</option>
                                <option value="$10">$10</option>
                                <option value="$20">$20</option>
                                <option value="$50">$50</option>
                            </Field>
                        </div>

                        <p>
                            Please pay your fees by direct debit to the CFBS
                            bank account <strong>before the school</strong>.
                            Note that we no longer require a deposit. <br />
                            <span className="small">
                                Account No. 03-0539-0226329-00 Remember to
                                include your name in the reference.
                            </span>
                        </p>
                        <p className="fees-note">
                            Registration is not final until fees have been paid.
                            If you need assistance with fees, please contact us
                            at programme@cfbs.org.nz
                        </p>
                        <h2>Register others</h2>
                        <p className="small">
                            <strong>Please note:</strong> you can only register
                            other members of your household on a single form. To
                            register people from a different address please
                            complete a separate form.
                        </p>
                        <div className="numbers">
                            <label>
                                How many other people do you wish to register?
                            </label>
                            <NumericFormat
                                value={attendeeCount}
                                onValueChange={(values) =>
                                    updateAttendeeCount(values.floatValue || 0)
                                }
                                isAllowed={(values) => {
                                    const { floatValue } = values;
                                    return floatValue >= 0 && floatValue <= 15;
                                }}
                                allowNegative={false}
                                decimalScale={0}
                                className="form-control"
                            />
                        </div>
                        {[...Array(attendeeCount).keys()].map(
                            function (item, i) {
                                const attendeeIndex = i + 1;
                                return (
                                    <div key={i}>
                                        <h2>Applicant {i + 2}</h2>
                                        <div className="form-field">
                                            <label>First name</label>
                                            <Field
                                                name={`attendees[${attendeeIndex}].firstName`}
                                                validate={validateRequired}
                                                className="required"
                                            />
                                            <ErrorMessage
                                                name={`attendees[${attendeeIndex}].firstName`}
                                                component="div"
                                                className="validation-error"
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label>Last name</label>
                                            <Field
                                                name={`attendees[${attendeeIndex}].lastName`}
                                                validate={validateRequired}
                                                className="required"
                                            />
                                            <ErrorMessage
                                                name={`attendees[${attendeeIndex}].lastName`}
                                                component="div"
                                                className="validation-error"
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label>Email</label>
                                            <Field
                                                name={`attendees[${attendeeIndex}].email`}
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label>
                                                Subscribe to Bible School Email
                                                Updates?
                                            </label>
                                            <Field
                                                component="select"
                                                name={`attendees[${attendeeIndex}].subscribe`}
                                            >
                                                <option value="">--</option>
                                                <option value="yes">Yes</option>
                                                <option value="no">No</option>
                                            </Field>
                                        </div>
                                        <div className="form-field">
                                            <label>Phone</label>
                                            <Field
                                                name={`attendees[${attendeeIndex}].phone`}
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label>Sex</label>
                                            <Field
                                                component="select"
                                                name={`attendees[${attendeeIndex}].gender`}
                                                validate={validateRequired}
                                                className="required"
                                            >
                                                <option value="">--</option>
                                                <option value="male">
                                                    Male
                                                </option>
                                                <option value="female">
                                                    Female
                                                </option>
                                            </Field>
                                            <ErrorMessage
                                                name={`attendees[${attendeeIndex}].gender`}
                                                component="div"
                                                className="validation-error"
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label>Dietary Requirements</label>
                                            <Field
                                                name={`attendees[${attendeeIndex}].dietaryRequirements`}
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label>Age</label>
                                            <Field
                                                component="select"
                                                name={`attendees[${attendeeIndex}].age`}
                                                validate={validateRequired}
                                                className="required"
                                            >
                                                <option value="">--</option>
                                                <option value="50orOver">
                                                    50 or over
                                                </option>
                                                <option value="26-49">
                                                    26-49
                                                </option>
                                                <option value="18-25">
                                                    18-25
                                                </option>
                                                <option value="under18">
                                                    Under 18
                                                </option>
                                            </Field>
                                            <ErrorMessage
                                                name={`attendees[${attendeeIndex}].age`}
                                                component="div"
                                                className="validation-error"
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label>
                                                Date of Birth (if 25 or under){' '}
                                                <span className="small">
                                                    dd/mm/yy
                                                </span>
                                            </label>
                                            <div className="guardian">
                                                <Field
                                                    name={`attendees[${attendeeIndex}].dateOfBirth`}
                                                />
                                                <span className="small">
                                                    Used to calculate fees,
                                                    allocate Junior School
                                                    classes and plan young
                                                    people's activities.
                                                </span>
                                            </div>
                                        </div>
                                        <div className="form-field">
                                            <label>Guardian</label>
                                            <div className="guardian">
                                                <Field
                                                    name={`attendees[${attendeeIndex}].guardian`}
                                                />
                                                <span className="small">
                                                    15-17 year olds attending
                                                    without their parents must
                                                    nominate another attendee as
                                                    a guardian. Attendance is
                                                    subject to approval.
                                                </span>
                                            </div>
                                        </div>
                                        <div className="form-field">
                                            <label>
                                                School Year (if applicable)
                                            </label>
                                            <Field
                                                name={`attendees[${attendeeIndex}].schoolYear`}
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label>Tertiary student</label>
                                            <Field
                                                component="select"
                                                name={`attendees[${attendeeIndex}].tertiaryStudent`}
                                            >
                                                <option value="">--</option>
                                                <option value="yes">Yes</option>
                                                <option value="no">No</option>
                                            </Field>
                                        </div>
                                        <div className="form-field">
                                            <label>
                                                First Aid Certificate?
                                            </label>
                                            <Field
                                                component="select"
                                                name={`attendees[${attendeeIndex}].firstAidCert`}
                                            >
                                                <option value="">--</option>
                                                <option value="yes">Yes</option>
                                                <option value="no">No</option>
                                            </Field>
                                        </div>
                                        <div className="form-field">
                                            <label>Baptised</label>
                                            <Field
                                                component="select"
                                                name={`attendees[${attendeeIndex}].baptised`}
                                                validate={validateRequired}
                                                className="required"
                                            >
                                                <option value="">--</option>
                                                <option value="yes">Yes</option>
                                                <option value="no">No</option>
                                            </Field>
                                            <ErrorMessage
                                                name={`attendees[${attendeeIndex}].baptised`}
                                                component="div"
                                                className="validation-error"
                                            />
                                        </div>
                                        {/* <div className="form-field">
                                        <label>If you are asked to be involved with Junior School, do you agree to completing a Police Check and a short training session for child safety purposes? </label>
                                        <div className="guardian">
                                        <Field
                                            component="select"
                                            name={`attendees[${attendeeIndex}].policeCheck`}
                                            
                                        >
                                            <option value="">--</option>
                                            <option value="yes">Yes</option>
                                            <option value="no">No</option>
                                            <option value="n/a">Not applicable</option>
                                        </Field>
                                        <div className="small">Note, the school will pay for these.</div>
                                        </div>
                                        
                                    </div> */}
                                        <div className="form-field">
                                            <label>Accommodation</label>
                                            <Field
                                                component="select"
                                                name={`attendees[${attendeeIndex}].accommodation`}
                                                validate={validateRequired}
                                                className="required"
                                            >
                                                <option value="">--</option>
                                                <option value="room">
                                                    Room required
                                                </option>
                                                <option value="tent-caravan">
                                                    Bringing a tent or caravan
                                                </option>
                                                <option value="off-site">
                                                    Staying off site
                                                </option>
                                            </Field>
                                            <ErrorMessage
                                                name={`attendees[${attendeeIndex}].accommodation`}
                                                component="div"
                                                className="validation-error"
                                            />
                                        </div>
                                    </div>
                                );
                            }
                        )}
                        <div className="buttons">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="submit-button"
                                onClick={() => {
                                    setSubmissionError(false);
                                    if (!agreeToRules) {
                                        setRulesError(true);
                                    }
                                }}
                            >
                                Submit
                            </button>
                            {submissionError && (
                                <div className="errors">
                                    <strong>
                                        Sorry, Something went wrong. Please try
                                        again.
                                    </strong>
                                </div>
                            )}
                            {(errors.attendees ||
                                errors.mainFields ||
                                !agreeToRules) && (
                                <div className="errors">
                                    Your form cannot be submitted because you
                                    have not completed the some required fields.{' '}
                                    <br />
                                    Please scroll up and complete all of the
                                    fields with{' '}
                                    <span style={{ color: 'red' }}>
                                        'Required!'
                                    </span>{' '}
                                    and try again
                                </div>
                            )}
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default EnrolmentForm;
